import { CategoryMapping, raiCategoryMapping, stepsUtilData } from "@/lib/utils";
import { RAIResponse } from ".";
import { handleGenerateOutputRequest } from "@/app/actions/actions";
import { ListItemProps, PDPGenerateOutputPayload } from "../../types";
import { FieldConfig } from "@/components/InputFields";
import { InputFormValues } from "./types";
import { RaiFailedCompliancesProps } from "@/components/RaiErrorAccordion";
import { LANGUAGE } from "@/app/(protected)/asset-generation/components/ReviewAssets/components/constants";
import { PROJECT_TYPES } from "@/constants/constants";
import { Images } from "@/app/(protected)/concepting/components/StepFour/types";
import { logger } from "@/lib/client-logger";

export const SAVE_AND_CLOSE = "Save and close";
export const GENERATE_IMAGES = "Generate Images";
export const CANCEL = "Cancel";
export const ASPECT_RATIO_LABEL = "Select the size/aspect ratio of the image to output";
export const TEXT_ON_IMAGE_LABEL = "Add text to your image:";
export const LOGO_ON_IMAGE_LABEL = "Would you like to add a logo on your image?";
export const TEXT_INPUT_LABEL = "Enter copy you want to display on the image";
export const DESCRIPTION_LABEL = "Describe the image you want in detail";
export const EMPTY_DESCRIPTION = "You must describe the image you want to output.";
export const UPLOAD_IMAGE_LABEL = "ADD A REFERENCE IMAGE (OPTIONAL)";
export const UPLOAD_IMAGE_SUBTEXT = "By uploading this image, you are certifying that all proper usage rights have been obtained.";
export const RAI_WARNING_MODAL_LABEL = "We recommend making edits now";
export const RAI_WARNING_MODAL_DESCRIPTION =
  "Your input has not passed the Responsible AI check and the flagged elements may affect the quality of your output. By choosing to proceed, you accept all responsibility for the results.";
export const RAI_WARNING_MODAL_LIFESTYLE =
  "Your input has not passed the Responsible AI check and flagged elements may affect the quality of your output. By choosing to proceed, you accept all responsibility for the results.";
export const BUTTON_TEXT_AGREE_AND_PROCEED = "Agree & proceed";
export const BUTTON_TEXT_EDIT = "Edit";
export const DESCRIPTION = "description";
export const ASPECT_RATIO = "aspectRatio";
export const TEXTONIMAGE = "textOnImage";
export const TEXT_INPUT = "textInput";
export const LOGOONIMAGE = "logoOnImage";
export const GENERATE_TEXT_BUTTON = "Generate text";
export const PRODUCT_PLACEMENT_OPTION = "productImageOption";
export const PRODUCT_PLACEMENT_VALUE = "productPlacementImageValue";
export const REFERENCE_IMAGE_CHECK = "referenceImageCheck";
export const REFERENCE_IMAGE_URL = "referenceImageUrl";
export const IMAGE_NOTES_OR_COMMENTS = "imageNotesOrComments";
export const SELECTED_PRODUCT_IMAGE_URL = "selectedProductImageUrl";
export const ASPECT_RATIO_ERROR = "Please select aspect ratio.";
export const SOURCE_TEXT = {
  GENERATE: "generate",
  SAVE: "save",
};
export const CHECKBOX_ERROR = "Confirm that you have usage rights to proceed.";
export const TEXT_INPUT_ERROR = "Text input is required when 'yes' is selected for text on image.";

export const aspectRatioOptions = [
  { id: "1024 x 1024 px (1:1)", label: "1024 x 1024 px (1:1)" },
  { id: "896 x 1280 px (3:4)", label: "896 x 1280 px (3:4)" },
  { id: "1280 x 896 px (4:3)", label: "1280 x 896 px (4:3)" },
  { id: "768 x 1408 px (9:16)", label: "768 x 1408 px (9:16)" },
  { id: "1408 x 768 px (16:9)", label: "1408 x 768 px (16:9)" },
];

export const textOnImageOptions = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];

const MAX_CHAR_LIMIT = 30;
export const TEXT_INPUT_CHAR_LIMIT_ERROR = `There is maximum character limit of ${MAX_CHAR_LIMIT}.`;

const getRaiDataFallback = (raiResponse: RAIResponse[]) => {
  // Old saved projects will return only the complianceIds because they do not have the `failedCompliances` key saved in DB.
  return raiResponse?.[0]?.complianceIds?.map((complianceId) => {
    return { complianceId };
  });
};

export function getRAIStatus(raiResponse: RAIResponse[], type: string, field?: string, isNewFeature?: false): string;
export function getRAIStatus(
  raiResponse: RAIResponse[],
  type: string,
  field: string | undefined,
  isNewFeature: true,
): RaiFailedCompliancesProps[];
export function getRAIStatus(
  raiResponse: RAIResponse[],
  type: string,
  field: string | undefined = undefined,
  isNewFeature: boolean = false,
): string | RaiFailedCompliancesProps[] {
  const filteredResponse = raiResponse?.filter((data) => data?.assetType === type && (!field || data.assetText === field));
  // Keep this new logic after the new RAI Accordion Error msg component is integrated across the app
  if (isNewFeature) {
    return filteredResponse?.flatMap((data) => data?.failedCompliances ?? getRaiDataFallback(raiResponse)) as RaiFailedCompliancesProps[];
  }

  // Remove once the RAI accordion is implemented across the app
  const errorMessages = filteredResponse?.flatMap((data) =>
    data?.complianceIds?.map((rai) => raiCategoryMapping[rai as keyof CategoryMapping]),
  );
  const uniqueMessages = Array.from(new Set(errorMessages));
  return uniqueMessages && uniqueMessages.length > 0 ? uniqueMessages.join(", ") : "";
}

export const generateOutputAPI = async (payload: PDPGenerateOutputPayload, projectType?: string, projectId?: string) => {
  try {
    const generateConceptOutput = await handleGenerateOutputRequest(projectType ?? "pdp", projectId ?? stepsUtilData.projectId, payload);
    stepsUtilData.version = generateConceptOutput.data.version;
    if (projectType === PROJECT_TYPES.GENERATE_ASSETS) {
      const language = LANGUAGE.ENGLISH;
      return generateConceptOutput.data.generated_outputs.modelOutputs?.languages?.[language]?.imageGroup ?? [];
    } else if (projectType === PROJECT_TYPES.BACKGROUND) {
      return generateConceptOutput.data.generated_outputs.modelOutputs?.imageGroup ?? [];
    } else {
      return generateConceptOutput.data.generated_outputs.modelOutputs?.images ?? [];
    }
  } catch (err) {
    logger.error("Error On generate Output", err);
    throw err;
  }
};

export type LifestyleGeneratedOutput = {
  output: Images[];
  originalOutput: Images[];
};

export const generateLifestyleOutputAPI = async (payload: PDPGenerateOutputPayload, projectId?: string): Promise<LifestyleGeneratedOutput> => {
  try {
    const generateConceptOutput = await handleGenerateOutputRequest(PROJECT_TYPES.PDP, projectId ?? stepsUtilData.projectId, payload);
    stepsUtilData.version = generateConceptOutput.data.version;
    const modelOutputs = generateConceptOutput.data.generated_outputs.modelOutputs;
    const output = modelOutputs?.images ?? [];
    const originalOutput = modelOutputs?.originalImages ?? output;

    return {
      output,
      originalOutput,
    };
  } catch (err) {
    logger.error("Error On generate lifestyle output", err);
    throw err;
  }
};

export const isDataChanged = (
  cachedData: ListItemProps,
  description: string,
  imageUrl?: string,
  textInput?: string,
  referenceImageDescription?: string,
): boolean => {
  const isSameDescription = cachedData?.description === description;
  const isSameImageUrl = cachedData?.imageUrl === (imageUrl ?? "");
  const isSameTextInput = cachedData?.textOnImage === (textInput ?? "");
  const isSameReferenceImageDescription = cachedData?.referenceImageDescription === (referenceImageDescription ?? "");

  return !(isSameDescription && isSameImageUrl && isSameTextInput && isSameReferenceImageDescription);
};

export const getTextOnImageField = (defaultImageField: string) => {
  const fieldName = defaultImageField === TEXTONIMAGE ? ("textOnImage" as const) : ("logoOnImage" as const);
  const label = defaultImageField === TEXTONIMAGE ? TEXT_ON_IMAGE_LABEL : LOGO_ON_IMAGE_LABEL;

  return [
    {
      name: fieldName,
      type: "radio" as const,
      label,
      options: textOnImageOptions,
      classNames: "",
      radioGroupClassName: "",
      optionLabelClassName: "normal-case",
      useFieldset: true,
    },
  ];
};

export const aspectRatioField: FieldConfig<InputFormValues>[] = [
  {
    name: ASPECT_RATIO,
    type: "radio",
    label: ASPECT_RATIO_LABEL,
    options: aspectRatioOptions,
    classNames: "mb-6",
  },
];
